import type { ReactNode } from 'react';
import { Tooltip } from '../../../components/common/Tooltip';

interface Props {
  folder: 'shared' | 'personal';
  /** この商品に紐付いたフォルダ内素材の件数 */
  materialCount: number;
  /** 商品画像の総数 */
  totalImages: number;
  /** まだこのフォルダにアップロードしていない商品画像数 */
  pendingCount: number;
  /** このフォルダへ一括アップロード実行中 */
  isUploading: boolean;
  /** いずれかのフォルダへアップロード中 (ボタン無効化) */
  uploadDisabled: boolean;
  /** ? ツールチップの説明文 */
  helpText: string;
  /** 素材数行のラベル (例: この商品の素材数) */
  countLabel: string;
  /** アップロードツールチップで使う画像の呼称 (例: 商品画像) */
  imageNoun: string;
  onUpload: () => void;
  /** フォルダを素材パネルで開く (商品フィルタ付き) */
  onOpen: () => void;
}

/**
 * ヘッダーのフォルダ別カード:
 *   上段 = フォルダ内の素材数 (閲覧) / 下段 = 商品画像の一括アップロード
 * 「商品画像 (登録した元画像)」と「素材 (フォルダに入れて使える状態)」を
 * 明確に分け、ヘルプツールチップでその関係を説明する。
 * Amazon / マニュアル商品詳細ページで共用する。
 */
export function MaterialFolderCard({
  folder,
  materialCount,
  totalImages,
  pendingCount,
  isUploading,
  uploadDisabled,
  helpText,
  countLabel,
  imageNoun,
  onUpload,
  onOpen,
}: Props) {
  const isShared = folder === 'shared';
  const modifier = isShared ? 'shared' : 'personal';
  const folderName = isShared ? '共有フォルダ' : '個人フォルダ';
  // 見出し: 素材パネルの共有/個人フォルダタブと同じアイコン
  const folderIcon = isShared ? '/cloud_file.svg' : '/single_neutral_folder_box.svg';
  // アップロードボタン: 各画像の「共有/個人フォルダに保存」ボタンと同じアイコン
  const uploadIcon = isShared ? '/cloud_add.svg' : '/folder_plus.svg';

  let uploadLabel: ReactNode;
  if (totalImages === 0) uploadLabel = '画像なし';
  else if (pendingCount === 0) uploadLabel = 'アップロード済み';
  else {
    // 狭いモバイルでは「…をアップ」に省略するため、末尾の「ロード」を
    // 個別 span に切り出して CSS のメディアクエリで隠す。
    const uploadPrefix = pendingCount === totalImages
      ? `全${pendingCount}枚をアップ`
      : `残り${pendingCount}枚をアップ`;
    uploadLabel = (
      <span className="mfc-upload-label">
        {uploadPrefix}
        <span className="mfc-upload-suffix">ロード</span>
      </span>
    );
  }

  // カード全体が「フォルダを開く」ボタン (素材が 1 件以上のとき)。
  const openable = materialCount > 0;
  const openFolder = () => { if (openable) onOpen(); };
  return (
    <div
      className={`material-folder-card material-folder-card--${modifier}${openable ? ' is-clickable' : ''}`}
      onClick={openable ? openFolder : undefined}
      role={openable ? 'button' : undefined}
      tabIndex={openable ? 0 : undefined}
      onKeyDown={openable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFolder(); } } : undefined}
      title={openable ? `${folderName}の素材を開く` : undefined}
    >
      <div className="mfc-head">
        <span className="mfc-title">
          <img src={folderIcon} alt="" className="mfc-title-icon" />
          {folderName}
        </span>
        <Tooltip content={helpText} placement="bottom" className="mfc-help-wrap">
          <span className="mfc-help" aria-label="このフォルダの説明" onClick={(e) => e.stopPropagation()}>?</span>
        </Tooltip>
      </div>

      {/* フォルダ内の素材 (= アップロードして使える状態のもの) */}
      <div className="mfc-row">
        <span className="mfc-row-label">{countLabel}</span>
        <span className="mfc-count">{materialCount >= 1000 ? '999+' : materialCount}</span>
      </div>

      {/* 商品画像 (= 登録した元画像) → アップロード */}
      <Tooltip
        content={pendingCount === 0 ? '新たにアップロードする画像はありません' : `${folderName}には無い${imageNoun}${pendingCount}枚をアップロード`}
        placement="bottom"
        className="mfc-upload-tip"
      >
        <button
          type="button"
          className={`mfc-upload mfc-upload--${modifier}`}
          onClick={(e) => { e.stopPropagation(); onUpload(); }}
          disabled={uploadDisabled || pendingCount === 0}
        >
          {isUploading ? (
            <>
              <span className="saving-spinner" />
              アップロード中...
            </>
          ) : (
            <>
              <img src={uploadIcon} alt="" className="mfc-upload-icon" />
              {uploadLabel}
            </>
          )}
        </button>
      </Tooltip>
    </div>
  );
}
